import { createEntityAdapter, createSlice, PayloadAction, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { SalesChannel } from '@cdaas/api-clients/dist/product-service-openapi';

import { RootState } from '.';
import { EntriesState, Loading, initialEntriesState } from '../utils/store';
import { productServiceAdminSalesChannelsClient } from '../api-client/product-service-client';
import { resolveAxiosErrorResponse } from '../utils/errors';

interface SalesChannelsState extends EntriesState<SalesChannel> {
  currentSalesChannelId: SalesChannel['id'];
}

export const initialSalesChannelsState: SalesChannelsState = {
  ...initialEntriesState,
  currentSalesChannelId: 1,
};

const salesChannelsAdapter = createEntityAdapter<SalesChannel>();

// thunks

export const fetchSalesChannels = createAsyncThunk('salesChannels/fetchSalesChannelsStatus', async (_, { rejectWithValue }) => {
  try {
    const response = await productServiceAdminSalesChannelsClient.getSalesChannels();
    return response.data.sales_channels;
  } catch (error) {
    return rejectWithValue(resolveAxiosErrorResponse(error)?.statusText);
  }
});

// slice

const salesChannels = createSlice({
  name: 'salesChannels',
  initialState: salesChannelsAdapter.getInitialState(initialSalesChannelsState),
  reducers: {
    addOneSalesChannel: salesChannelsAdapter.addOne,
    updateOneSalesChannel(state, action: PayloadAction<SalesChannel>) {
      salesChannelsAdapter.updateOne(state, { id: action.payload.id, changes: action.payload });
    },
    removeOneSalesChannel: salesChannelsAdapter.removeOne,
    updateCurrentSalesChannelId(state, action: PayloadAction<SalesChannel['id']>) {
      state.currentSalesChannelId = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchSalesChannels.pending, state => {
      state.loading = Loading.PENDING;
    });
    builder.addCase(fetchSalesChannels.fulfilled, (state, action) => {
      salesChannelsAdapter.setAll(state, action.payload);
      state.loading = Loading.SUCCEEDED;
    });
    builder.addCase(fetchSalesChannels.rejected, state => {
      state.loading = Loading.FAILED;
    });
  },
});

export const { addOneSalesChannel, updateOneSalesChannel, removeOneSalesChannel, updateCurrentSalesChannelId } = salesChannels.actions;

export default salesChannels.reducer;

// selectors

export const { selectAll: selectAllSalesChannels, selectById: selectSalesChannelById } = salesChannelsAdapter.getSelectors(
  (state: RootState) => state.salesChannels,
);

export const selectSalesChannelsLoading = (state: RootState) => state.salesChannels.loading;
export const selectCurrentSalesChannelId = (state: RootState) => state.salesChannels.currentSalesChannelId;
export const selectCurrentSalesChannel = (state: RootState) => selectSalesChannelById(state, selectCurrentSalesChannelId(state));

export const selectSalesChannelByUuid = createSelector(
  selectAllSalesChannels,
  (state: RootState, uuid: SalesChannel['uuid']) => uuid,
  (salesChannels, uuid) => salesChannels.find(salesChannel => salesChannel.uuid === uuid),
);
